import { useState, useEffect, useCallback } from 'react';
import { useAppStore, recalculateStore } from '@/store/useAppStore';
import { listSnapshots, loadSnapshot, deleteSnapshot, compareSnapshots, saveSnapshot } from '@/store/versionHistory';
import type { SessionData } from '@gpa-scales/shared-types';

interface SnapshotInfo {
  id: string;
  label: string;
  createdAt: string;
  courseCount: number;
}

export function VersionHistory() {
  const [snapshots, setSnapshots] = useState<SnapshotInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [compareId, setCompareId] = useState<string | null>(null);
  const [compareResult, setCompareResult] = useState<{
    label1: string;
    label2: string;
    courseCountDiff: number;
    settingsChanged: boolean;
  } | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [confirmRestoreId, setConfirmRestoreId] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 10;

  const courses = useAppStore((s) => s.courses);
  const settings = useAppStore((s) => s.settings);

  const refreshSnapshots = useCallback(async () => {
    try {
      const list = await listSnapshots();
      setSnapshots(
        list.map((s) => ({
          id: s.id,
          label: s.label,
          createdAt: s.createdAt,
          courseCount: s.data.courses.length,
        }))
      );
    } catch {
      setSnapshots([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    refreshSnapshots();
  }, [refreshSnapshots]);

  const handleSaveSnapshot = useCallback(async () => {
    const data: SessionData = {
      version: 1,
      courses,
      settings,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    await saveSnapshot(data);
    refreshSnapshots();
  }, [courses, settings, refreshSnapshots]);

  const handleRestore = useCallback(
    async (id: string) => {
      const data = await loadSnapshot(id);
      if (data) {
        useAppStore.getState().importCourses(data.courses);
        useAppStore.getState().loadSettings(data.settings);
        recalculateStore();
      }
      setConfirmRestoreId(null);
    },
    []
  );

  const handleDelete = useCallback(
    async (id: string) => {
      await deleteSnapshot(id);
      setConfirmDeleteId(null);
      refreshSnapshots();
    },
    [refreshSnapshots]
  );

  const handleCompare = useCallback(
    async (id1: string, id2: string) => {
      const result = await compareSnapshots(id1, id2);
      if (result) {
        setCompareResult({
          label1: result.label1,
          label2: result.label2,
          courseCountDiff: result.courseCountDiff,
          settingsChanged: result.settingsChanged,
        });
      }
      setCompareId(null);
    },
    []
  );

  if (loading) {
    return (
      <div className="rounded-lg border border-border bg-surface p-8 text-center">
        <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-3 border-primary border-t-transparent" />
        <p className="text-sm text-[var(--color-text-muted)]">Loading snapshots…</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-surface p-4">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-[var(--color-text)]">Version History</h3>
        <button
          onClick={handleSaveSnapshot}
          className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-hover"
        >
          Save Snapshot
        </button>
      </div>

      {snapshots.length === 0 ? (
        <p className="text-sm text-[var(--color-text-muted)]">
          No snapshots yet. Click &ldquo;Save Snapshot&rdquo; to preserve the current courses and settings so you can return to them later.
        </p>
      ) : (
        <div className="space-y-2">
          {snapshots.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE).map((snap) => (
            <div
              key={snap.id}
              className="flex items-center justify-between rounded-md border border-border px-3 py-2"
            >
              <div>
                <p className="text-sm font-medium text-[var(--color-text)]">{snap.label}</p>
                <p className="text-xs text-[var(--color-text-muted)]">
                  {new Date(snap.createdAt).toLocaleString()} · {snap.courseCount} courses
                </p>
              </div>
              <div className="flex items-center gap-1">
                {compareId && compareId !== snap.id ? (
                  <button
                    onClick={() => handleCompare(compareId, snap.id)}
                    className="rounded px-2 py-1 text-xs text-primary hover:bg-primary-light"
                  >
                    Compare
                  </button>
                ) : (
                  <button
                    onClick={() => setCompareId(snap.id)}
                    className="rounded px-2 py-1 text-xs text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)]"
                  >
                    Select to Compare
                  </button>
                )}
                {confirmRestoreId === snap.id ? (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleRestore(snap.id)}
                      className="rounded bg-success px-2 py-1 text-xs text-white"
                    >
                      Restore
                    </button>
                    <button
                      onClick={() => setConfirmRestoreId(null)}
                      className="rounded px-2 py-1 text-xs text-[var(--color-text-muted)]"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setConfirmRestoreId(snap.id)}
                    className="rounded px-2 py-1 text-xs text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)]"
                  >
                    Restore
                  </button>
                )}
                {confirmDeleteId === snap.id ? (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleDelete(snap.id)}
                      className="rounded bg-error px-2 py-1 text-xs text-white"
                    >
                      Delete
                    </button>
                    <button
                      onClick={() => setConfirmDeleteId(null)}
                      className="rounded px-2 py-1 text-xs text-[var(--color-text-muted)]"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setConfirmDeleteId(snap.id)}
                    className="rounded px-2 py-1 text-xs text-error hover:bg-error-light"
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          ))}
          {snapshots.length > PAGE_SIZE && (
            <div className="flex items-center justify-center gap-2 pt-2">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="rounded px-2 py-1 text-xs text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)] disabled:opacity-50"
              >
                Previous
              </button>
              <span className="text-xs text-[var(--color-text-muted)]">
                Page {page + 1} of {Math.ceil(snapshots.length / PAGE_SIZE)}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(Math.ceil(snapshots.length / PAGE_SIZE) - 1, p + 1))}
                disabled={page >= Math.ceil(snapshots.length / PAGE_SIZE) - 1}
                className="rounded px-2 py-1 text-xs text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)] disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {compareResult && (
        <div className="mt-4 rounded-md border border-border bg-[var(--color-bg-tertiary)] p-3">
          <h4 className="mb-2 text-xs font-semibold text-[var(--color-text)]">Comparison</h4>
          <p className="text-xs text-[var(--color-text-muted)]">
            <strong>{compareResult.label1}</strong> vs <strong>{compareResult.label2}</strong>
          </p>
          <p className="text-xs text-[var(--color-text-muted)]">
            Course count difference: {compareResult.courseCountDiff > 0 ? '+' : ''}
            {compareResult.courseCountDiff}
          </p>
          <p className="text-xs text-[var(--color-text-muted)]">
            Settings changed: {compareResult.settingsChanged ? 'Yes' : 'No'}
          </p>
          <button
            onClick={() => setCompareResult(null)}
            className="mt-2 text-xs text-primary hover:underline"
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
}
